/**
 * Notification inbox 的客户端单例持有 + 缓存清理。
 *
 * 为什么单独一个文件：`clearNotificationInboxCache()` 的唯一调用方是
 * `src/api/auth.ts`（登出 / token 过期）。它原本定义在
 * `notification-inbox-web-component.tsx`（109 KB React 组件）里，于是 api 层
 * 为了一个清缓存函数急切拉进 `@novu/js` / `@novu/react` / 6 个 Radix /
 * lucide / sonner / react-i18next / react-router-dom —— 而 `@/api` barrel 被
 * 全仓 ~270 个文件消费，这些包就成了几乎所有模块图的一部分。
 *
 * 本文件只有 `import type`（编译期擦除），运行时依赖为零，api 层可以放心 import。
 * 不变量由 `src/api/__tests__/apiBarrelGraph.test.ts` 守着。
 */
import type { NovuClientWrapper } from './novu-client-wrapper'

// Singleton client wrapper instance (persists across component mounts)
let clientWrapperInstance: NovuClientWrapper | null = null

export function getNotificationClientWrapper(): NovuClientWrapper | null {
  return clientWrapperInstance
}

export function setNotificationClientWrapper(wrapper: NovuClientWrapper | null): void {
  clientWrapperInstance = wrapper
}

/**
 * Clear notification inbox cache and reset client wrapper
 * Call this function when user logs out or token expires
 */
export function clearNotificationInboxCache(): void {
  // Clear in-memory cache
  if (clientWrapperInstance) {
    clientWrapperInstance.clearAllCache()
  }

  // Reset the singleton instance to null
  // This forces a complete re-initialization on next login
  clientWrapperInstance = null

  // Clear Novu SDK's IndexedDB cache
  // Novu stores cache in IndexedDB under database name 'novu-db'
  try {
    const dbName = 'novu-db'
    const deleteRequest = indexedDB.deleteDatabase(dbName)

    deleteRequest.onerror = (event) => {
      console.error('[NotificationInbox] Failed to clear IndexedDB:', event)
    }

    deleteRequest.onblocked = () => {
      console.warn('[NotificationInbox] IndexedDB deletion blocked, may need to close all tabs')
    }
  } catch (error) {
    console.error('[NotificationInbox] Error clearing IndexedDB:', error)
  }
}
